import { DistanceUnit } from "shared/types";

// The only countries that use imperial distance units in everyday life.
// Region codes are ISO 3166-1 alpha-2.
const IMPERIAL_REGIONS = new Set(["US", "LR", "MM"]);

/**
 * Normalizes a free-text country string (as stored on an organization's
 * address) to imperial/metric. Handles the common ways "United States" gets
 * entered: "US", "USA", "U.S.", "United States", "United States of America",
 * plus Liberia and Myanmar. Returns null when the country is empty or
 * unrecognized so callers can fall back to another signal.
 */
export const distanceUnitFromCountry = (
  country: string | null | undefined,
): DistanceUnit | null => {
  if (!country) {
    return null;
  }
  const normalized = country.trim().toLowerCase().replace(/[.\s]/g, "");
  if (
    normalized === "us" ||
    normalized === "usa" ||
    normalized === "unitedstates" ||
    normalized === "unitedstatesofamerica" ||
    normalized === "liberia" ||
    normalized === "lr" ||
    normalized === "myanmar" ||
    normalized === "burma" ||
    normalized === "mm"
  ) {
    return "imperial";
  }
  // Any other recognizable country string is treated as metric.
  return "metric";
};

/**
 * Reads the visitor's regional preference from the browser and maps it to a
 * distance unit. Uses the region subtag of the browser locale (e.g. the "US"
 * in "en-US"), which reflects the user's OS region setting. Returns null when
 * no locale/region is available (e.g. non-browser environment, or a
 * language-only locale like "en") so callers can fall back.
 */
export const distanceUnitFromBrowser = (): DistanceUnit | null => {
  if (typeof navigator === "undefined") {
    return null;
  }
  const locales =
    navigator.languages && navigator.languages.length > 0
      ? navigator.languages
      : navigator.language
        ? [navigator.language]
        : [];

  for (const locale of locales) {
    let region: string | undefined;
    try {
      region = new Intl.Locale(locale).region ?? undefined;
    } catch {
      // Malformed locale tag — fall back to naive parsing below.
      region = undefined;
    }
    if (!region) {
      const parts = locale.split("-");
      // A 2-letter second subtag is the region (e.g. "en-US"); skip script
      // subtags like the "Hant" in "zh-Hant-TW".
      region = parts.find((part, i) => i > 0 && /^[A-Za-z]{2}$/.test(part));
    }
    if (region) {
      return IMPERIAL_REGIONS.has(region.toUpperCase()) ? "imperial" : "metric";
    }
  }
  return null;
};

/**
 * Resolves the distance unit for a Tripper visitor. The visitor's own browser
 * region wins (a US traveler should see miles regardless of who built the
 * itinerary); when the browser gives no usable region we fall back to the
 * organization's configured country, then finally to metric.
 */
export const resolveDistanceUnit = (
  organizationCountry?: string | null,
): DistanceUnit => {
  return (
    distanceUnitFromBrowser() ??
    distanceUnitFromCountry(organizationCountry) ??
    "metric"
  );
};
